import { useId, useState, type FormEvent, type ReactNode } from 'react';
import { ChevronDown, RotateCcw, Search } from 'lucide-react';
import { cx } from '@/lib/cx';
import { Button } from './Button';

export interface SearchPanelProps {
  title?: string;
  defaultOpen?: boolean;
  /** 検索実行（form submit）。react-hook-form なら handleSubmit(fn) を渡す */
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onReset?: () => void;
  /** 折りたたみ時に表示する現在の条件の要約 */
  summary?: ReactNode;
  /** 検索条件の列数（既定 4）。4 列以上のときは 1 列目（事業実施者の選択欄。正式名称は最大 13 文字）を広く取る */
  columns?: 2 | 3 | 4 | 5;
  searching?: boolean;
  children: ReactNode;
}

/** 一覧画面の検索条件パネル（折りたたみ可）。Enter で検索 */
export function SearchPanel({ title = '検索条件', defaultOpen = true, onSubmit, onReset, summary, columns = 4, searching, children }: SearchPanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  return (
    <section className={cx('bs-search', open && 'is-open')}>
      <button type="button" className="bs-search__toggle" aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen((v) => !v)}>
        <Search size={16} aria-hidden />
        <span className="bs-search__title">{title}</span>
        {!open && summary && <span className="bs-search__summary">{summary}</span>}
        <ChevronDown size={16} className="bs-search__chevron" aria-hidden />
      </button>
      <form
        id={bodyId}
        hidden={!open}
        className="bs-search__body"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(e);
        }}
        onReset={(e) => {
          if (onReset) {
            e.preventDefault();
            onReset();
          }
        }}
      >
        <div className="bs-search__grid" style={{ gridTemplateColumns: columns >= 4 ? `minmax(0, 1.4fr) repeat(${columns - 1}, minmax(0, 1fr))` : `repeat(${columns}, minmax(0, 1fr))` }}>
          {children}
        </div>
        <div className="bs-search__actions">
          {onReset && (
            <Button type="reset" variant="ghost" icon={<RotateCcw size={15} />}>
              条件をクリア
            </Button>
          )}
          <Button type="submit" variant="primary" icon={<Search size={15} />} loading={searching}>
            検索
          </Button>
        </div>
      </form>
    </section>
  );
}
